import React, { useState, useEffect, useMemo } from "react";
import Datagrid from "@bit/the-glue.frontendcomponents.datagrid";
import { TableSortLabel } from "@material-ui/core";
import { HEADINGS } from "./constants";
import { useFetch } from "../../../hooks/fetch.hook";
import { archiveSite, getSiteById, modifySite } from "./_api";
import { ListButton } from "../../../ui/components/ListButton";
import { ListHeader } from "../../../ui/structures/ListHeader";
import { renderSitesRow } from "./helpers";
import { Loader } from "../../../ui/components/Loader";
import { Modal } from "../../../ui/components/Modal";
import { ConfirmDelete } from "../../../ui/components/ConfirmDelete";
import { info } from "../../../helpers/toasts";
import { getOrganisationSites } from "../../modules/Organisations/_api";
import { getSelectedId } from "../../../ui/helpers";
import { UnarchiveButton } from "../../../ui/components/UnarchiveButton";
import { AVAILABLE_ROLES, sortHeaderStyle } from "../../constants";
import { ArchiveCheckbox } from "../../../ui/components/ArchiveCheckbox";
import {
  modifySitesArray,
  modifySite as modifySiteObject,
} from "../../modules/Sites/helpers";
import { AccessControl } from "../../../ui/structures/AccessControl";

export const SitesList = ({
  data = [],
  handleNew,
  handleExpand,
  organisationId,
  setSitesList,
  organisationName,
}) => {
  const { request } = useFetch();

  const [selected, setSelected] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archived, setArchived] = useState(false);
  const [unArchiving, setUnarchiving] = useState(false);
  const [field, setField] = useState("");
  const [direction, setDirection] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    request(getOrganisationSites, organisationId, archived, field, direction)
      .then((data) => data && setSitesList(modifySitesArray(data)))
      .finally(() => {
        setArchivedLoading(false);
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [archived, field, direction]);

  const handleModalClose = () => setConfirmDeleteOpen(false);

  const handleOpenModal = (id) => {
    setConfirmDeleteOpen(true);
    setSelectedItem(id);
  };

  const unArchiveDisabled = useMemo(
    () => Object.values(selected).filter(Boolean).length !== 1,
    [selected]
  );

  const updateCardData = (item = {}) => {
    const index = data.findIndex((site) => (site || {}).id === (item || {}).id);
    setSitesList((state) => [
      ...state.slice(0, index),
      item,
      ...state.slice(index + 1),
    ]);
  };

  const handleArchive = (id, setSubmitting) => {
    setActionLoading(true);
    setConfirmDeleteOpen(false);
    request(archiveSite, id)
      .then((data) => {
        if (!data) return;
        if (archived) {
          request(getSiteById, id).then((site) => {
            if (!site) return;
            updateCardData(modifySiteObject(site));
          });
        } else {
          setSitesList((state) => state.filter((item) => id !== item.id));
        }
        info("Site has been archived!");
        setSelected({});
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const handleUnarchive = () => {
    const siteID = getSelectedId(selected);
    const status = (data.find(({ id }) => id === siteID) || {}).status;
    if (status !== "archived") return;
    setUnarchiving(true);
    setActionLoading(true);
    request(modifySite, { status: "active" }, siteID)
      .then((data) => {
        if (!data) return;
        updateCardData(modifySiteObject(data));
        setSelected({});
        info("Site has been unarchived!");
      })
      .finally(() => {
        setUnarchiving(false);
        setActionLoading(false);
      });
  };

  const showArchived = () => {
    setArchivedLoading(true);
    setArchived(!archived);
  };

  const renderButtons = () => (
    <>
      {archived && (
        <UnarchiveButton
          handleClick={handleUnarchive}
          disabled={unArchiveDisabled}
          loading={unArchiving}
        />
      )}
      <AccessControl
        desiredRoles={[
          AVAILABLE_ROLES.NATIONAL_ADMIN,
          AVAILABLE_ROLES.AGSAFE_ADMIN,
          AVAILABLE_ROLES.AGSAFE_STAFF,
          AVAILABLE_ROLES.RC_ACCESS,
        ]}
      >
        <div>
          <ListButton
            label="New"
            onClick={handleNew}
            data-testid="new"
            className="my-2"
          />
        </div>
      </AccessControl>
    </>
  );

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={sortHeaderStyle}
        className="px-5 text-nowrap"
        onClick={() => {
          setDirection(
            key !== field ? "desc" : direction === "desc" ? "asc" : "desc"
          );
          setField(key);
        }}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {key === field && <TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-12">
          <div className="bg-white rounded py-7 px-10">
            {actionLoading && <Loader isOpen={actionLoading} maxWidth="xs" />}
            {confirmDeleteOpen && (
              <Modal
                isOpen={confirmDeleteOpen}
                submitable
                onClose={handleModalClose}
                maxWidth="sm"
                modalContent={
                  <ConfirmDelete
                    handleClose={handleModalClose}
                    handleSubmit={handleArchive}
                    id={selectedItem}
                    name={
                      (data.find(({ id }) => id === selectedItem) || {}).name
                    }
                  />
                }
              />
            )}
            <ListHeader
              title={`${organisationName} Site List`}
              renderButtons={renderButtons}
            />
            <ArchiveCheckbox
              archivedLoading={archivedLoading}
              archived={archived}
              showArchived={showArchived}
            />
            <Datagrid
              data={data}
              headings={HEADINGS}
              renderRow={renderSitesRow}
              selected={selected}
              setSelected={setSelected}
              handleDelete={handleOpenModal}
              renderHeaderWithSorting={renderHeaderWithSorting}
              expandable
              handleExpand={handleExpand}
              deletable
              selectable
              loading={loading}
            />
          </div>
        </div>
      </div>
    </>
  );
};
